import { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { BottomNav } from './BottomNav';
import { ToastProvider } from './Toast';
import { ChatDrawer } from './ChatDrawer';
import { ChatFAB } from './ChatFAB';
import { NotificationCenter, type NotificationItem } from './NotificationCenter';
import { OutboundBanner } from './OutboundBanner';
import { WhatsAppReminderBanner } from './WhatsAppReminderBanner';
import { healthAPI, nudgesAPI, outboundAPI, reflectionsAPI } from '../api/client';
import { useManagedMode } from '../hooks/useManagedMode';
import type { OutboundSafetyStatus } from '../types';

// Wrapper to provide toast context
export function AppShell() {
  return (
    <ToastProvider>
      <AppShellContent />
    </ToastProvider>
  );
}

// Inner component
function AppShellContent() {
  const navigate = useNavigate();
  const { isManaged } = useManagedMode();
  const [chatOpen, setChatOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [outboundStatus, setOutboundStatus] = useState<OutboundSafetyStatus | null>(null);
  const [whatsappConnected, setWhatsappConnected] = useState(true);

  const refreshNotifications = useCallback(async () => {
    try {
      const [nudges, reflections, reviewItems, health] = await Promise.all([
        nudgesAPI.list({ status: 'active' }),
        reflectionsAPI.list(),
        outboundAPI.listReview({ limit: 20 }),
        healthAPI.detailed(),
      ]);
      setOutboundStatus(health.outbound ?? null);
      setWhatsappConnected(health.whatsapp?.status === 'connected');

      const nudgeItems: NotificationItem[] = nudges.slice(0, 5).map((nudge) => ({
        id: `nudge-${nudge.id}`,
        type: 'nudge',
        label: nudge.contact_name
          ? `${nudge.contact_name} needs follow-up`
          : 'Contact needs follow-up',
        detail: nudge.talking_point,
      }));
      const reflectionItems: NotificationItem[] = reflections.slice(0, 2).map((reflection) => ({
        id: `reflection-${reflection.week}`,
        type: 'reflection',
        label: `Weekly reflection ${reflection.week}`,
        detail: reflection.highlights[0] || 'New coaching reflection available.',
      }));
      const reviewItem: NotificationItem[] = reviewItems.length > 0
        ? [{
            id: 'review-queue',
            type: 'review_queue',
            label: `${reviewItems.length} outbound item${reviewItems.length === 1 ? '' : 's'} awaiting review`,
            detail: 'Approve, reject, or override queued outbound sends.',
          }]
        : [];
      const breakerItem: NotificationItem[] = health.outbound?.circuit_breaker.state === 'open'
        ? [{
            id: 'outbound-breaker',
            type: 'outbound_breaker',
            label: 'Outbound automation paused',
            detail: 'Circuit breaker is open. Resume from banner or settings.',
          }]
        : [];

      setNotifications([...breakerItem, ...reviewItem, ...nudgeItems, ...reflectionItems]);
    } catch {
      setNotifications([]);
    }
  }, []);

  useEffect(() => {
    void refreshNotifications();
    const interval = setInterval(() => {
      void refreshNotifications();
    }, 30000);
    return () => clearInterval(interval);
  }, [refreshNotifications]);

  const totalUnread = useMemo(() => notifications.length + chatUnread, [notifications.length, chatUnread]);

  function handleNotificationSelect(item: NotificationItem) {
    if (item.type === 'reflection') {
      const week = item.id.replace('reflection-', '');
      navigate(`/reflections?week=${week}`);
      setNotifications((prev) => prev.filter((n) => n.id !== item.id));
      return;
    }
    if (item.type === 'chat') {
      setChatOpen(true);
      setNotifications((prev) => prev.filter((n) => n.id !== item.id));
      return;
    }
    if (item.type === 'nudge') {
      navigate('/people');
      setNotifications((prev) => prev.filter((n) => n.id !== item.id));
      return;
    }
    if (item.type === 'review_queue' || item.type === 'outbound_breaker') {
      navigate('/people');
      setNotifications((prev) => prev.filter((n) => n.id !== item.id));
    }
  }

  return (
    <div className="min-h-[100dvh] bg-canvas text-ink pb-16 flex justify-center">
      <div className="w-full max-w-2xl flex flex-col">
        <div className="px-4 pt-2 flex justify-end">
          <NotificationCenter items={notifications} onSelect={handleNotificationSelect} />
        </div>
        <OutboundBanner outbound={outboundStatus} onResumed={() => void refreshNotifications()} />
        {isManaged && <WhatsAppReminderBanner whatsappConnected={whatsappConnected} />}
        {/* Main Content Area — use dvh to match visible viewport on iOS Safari */}
        <main className="h-[calc(100dvh-6rem)] overflow-hidden">
          <Outlet />
        </main>
      </div>

      <ChatFAB unreadCount={totalUnread} onClick={() => setChatOpen(true)} />
      <ChatDrawer
        isOpen={chatOpen}
        threadId="default-thread"
        onClose={() => setChatOpen(false)}
        onUnreadChange={setChatUnread}
      />

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
}

export default AppShell;
